import { Box, Flex, FormControl, FormHelperText, FormLabel, Spinner, Switch } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  SourcingEventStatus,
  useCompleteNegotiationRoundV2Mutation,
  useCompleteSourcingEventV2Mutation,
  useReopenCompletedSourcingEventV2Mutation,
  useReopenNegotiationRoundV2Mutation,
  type EvalProgressDto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useRoundSelection } from "../rounds/RoundSelectionContext";

/**
 * ADR-033 D5: V1's complete-evaluation toggle, mapped onto the existing lifecycle —
 * the selected round's complete/reopen, or the event's EVALUATION ↔ COMPLETED
 * transitions for the original competition. Completion freezes scores and the basket.
 */
export const CompleteEvaluationToggle = ({
  event,
  progress,
}: {
  event: SourcingEventV2Dto;
  progress: EvalProgressDto;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const { selectedRound } = useRoundSelection();

  const [completeEvent, { isLoading: isCompletingEvent }] = useCompleteSourcingEventV2Mutation();
  const [reopenEvent, { isLoading: isReopeningEvent }] = useReopenCompletedSourcingEventV2Mutation();
  const [completeRound, { isLoading: isCompletingRound }] = useCompleteNegotiationRoundV2Mutation();
  const [reopenRound, { isLoading: isReopeningRound }] = useReopenNegotiationRoundV2Mutation();
  const isLoading = isCompletingEvent || isReopeningEvent || isCompletingRound || isReopeningRound;

  // Once the event moves past evaluation the rounds are history — no completing or
  // reopening from here (the BFF rejects it too).
  const eventOpenForRounds =
    event.status === SourcingEventStatus.Published || event.status === SourcingEventStatus.Evaluation;
  const applicable = selectedRound
    ? selectedRound.status !== NegotiationRoundStatus.Draft && eventOpenForRounds
    : event.status === SourcingEventStatus.Evaluation || event.status === SourcingEventStatus.Completed;
  if (!applicable) return null;

  const isCompleted = selectedRound
    ? selectedRound.status === NegotiationRoundStatus.Completed
    : event.status === SourcingEventStatus.Completed;
  const scoresRemaining = Math.max(0, progress.total - progress.filled);
  const blockedByUnscoredBids = !isCompleted && scoresRemaining > 0;

  const onToggle = async (checked: boolean) => {
    try {
      if (selectedRound) {
        if (checked) {
          await completeRound({ eventId: event.id, roundId: selectedRound.id }).unwrap();
        } else {
          await reopenRound({ eventId: event.id, roundId: selectedRound.id }).unwrap();
        }
      } else if (checked) {
        await completeEvent({ eventId: event.id }).unwrap();
      } else {
        await reopenEvent({ eventId: event.id }).unwrap();
      }
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
      <FormControl display="flex" alignItems="center" justifyContent="space-between" gap="4">
        <Box>
          <FormLabel mb="0">{t("Complete Evaluation")}</FormLabel>
          <FormHelperText mt="1">
            {isCompleted
              ? t("The evaluation is completed")
              : blockedByUnscoredBids
                ? t("{{count}} scores are still missing. Score every bid on every criterion before completing the evaluation.", { count: scoresRemaining })
                : t("Toggle to complete the evaluation with the current price selection")}
          </FormHelperText>
        </Box>
        <Flex align="center" gap="2">
          {isLoading && <Spinner size="sm" color="smPrimary" />}
          <Switch
            size="lg"
            colorScheme="teal"
            isChecked={isCompleted}
            isDisabled={isLoading || blockedByUnscoredBids}
            onChange={(e) => onToggle(e.target.checked)}
          />
        </Flex>
      </FormControl>
    </Box>
  );
};
